/*
  pzz-grid-strips.js - keeps the full-width collection grid strips on a row boundary.
  2026-09-28 (linking plan step 5). Loaded only by sections/pzz-main-collection.liquid.

  WHY: Liquid inserts strips after a fixed card count (8, 16, 12...), but the desktop
  grid uses Horizon's "medium" card size, so the column count depends on screen width
  (5 at 1440px, fewer or more elsewhere). A strip after card 8 on a 5-column grid
  leaves a half-empty row. This moves each strip forward to the end of the row it
  falls in, for whatever column count is showing, and re-checks when the grid
  resizes (window width, Horizon's zoom-out toggle) or grows (infinite scroll).

  Each strip remembers the card it was rendered after (data-pzz-anchor) and is
  put back there before every re-alignment, so repeated resizes never drift.
  The end-of-grid strip (.pzz-gi--end) is left alone. Strips never move past the
  last card, so Horizon's infinite scroll still reads data-page from a product.
*/
(() => {
  const STRIP = 'pzz-gi';

  function columnCount(grid) {
    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }

  function isStrip(el) {
    return el.classList.contains(STRIP);
  }

  // Only real product cards count towards a row. Horizon puts an inline <script>
  // inside the grid (first child), which must be ignored or every strip lands one
  // card late (2026-09-28 fix: strips showed after card 9 instead of 8).
  function isCard(el) {
    return el.classList.contains('product-grid__item');
  }

  function align(grid) {
    const cols = columnCount(grid);
    const strips = [...grid.children].filter((el) => isStrip(el) && !el.classList.contains('pzz-gi--end'));
    if (!strips.length) return;

    // remember / restore original positions
    for (const s of strips) {
      if (!s.dataset.pzzAnchor) {
        let prev = s.previousElementSibling;
        while (prev && !isCard(prev)) prev = prev.previousElementSibling;
        if (prev && prev.id) s.dataset.pzzAnchor = prev.id;
      } else {
        const anchor = document.getElementById(s.dataset.pzzAnchor);
        if (anchor && anchor.parentElement === grid && anchor.nextElementSibling !== s) anchor.after(s);
      }
    }
    if (cols < 2) return;

    // walk the grid, pushing each strip to the end of its row
    let run = 0;
    let el = grid.firstElementChild;
    while (el) {
      const next = el.nextElementSibling;
      if (isCard(el)) {
        run += 1;
      } else if (isStrip(el)) {
        const short = run % cols ? cols - (run % cols) : 0;
        if (short && !el.classList.contains('pzz-gi--end')) {
          let target = null;
          let found = 0;
          let probe = next;
          while (probe && found < short) {
            if (isStrip(probe)) break;
            if (isCard(probe)) {
              target = probe;
              found += 1;
            }
            probe = probe.nextElementSibling;
          }
          // only move if a full row can be completed and a card still follows
          if (found === short && target && target.nextElementSibling && isCard(target.nextElementSibling)) {
            target.after(el);
            run = 0;
            el = el.nextElementSibling;
            continue;
          }
        }
        run = 0;
      }
      el = next;
    }
  }

  function watch(grid) {
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        observer.disconnect();
        align(grid);
        observer.observe(grid, { childList: true });
      });
    };
    const observer = new MutationObserver(schedule);
    observer.observe(grid, { childList: true });
    new ResizeObserver(schedule).observe(grid);
    schedule();
  }

  const watched = new WeakSet(); // not a data attribute: Horizon's morph can strip those

  function init() {
    document.querySelectorAll('.product-grid').forEach((grid) => {
      if (watched.has(grid)) return;
      watched.add(grid);
      watch(grid);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // filters/sorting re-render the section; pick up a replaced grid (throttled)
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      init();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
